import { useEffect, useState } from "react";
import { Flex } from "@chakra-ui/layout";
import { Modal, Card, Spin, Button, Table, Tooltip } from "antd";
import { MdAssignment, MdList, MdOutlineKeyboardArrowRight, MdKeyboardArrowDown } from "react-icons/md";
import { FaTrash, FaLeaf } from "react-icons/fa";
import styled from "styled-components";
import { showError, showSuccess, showConfirm } from "infra/helpers/alerta";
import LoadingCard from "../../../../LoadingCard";
import useGetPlanejamentosTalhao from "../../hooks/useGetPlanejamentosTalhao";
import useCancelarVinculoPlanejamentosTalhoes from "../../hooks/useCancelarVinculoPlanejamentosTalhoes";
import { useFilters } from "core/features/filters/store";
import getColumns from "./columns";
import useTalhoesStore from "../../hooks/useTalhoesStore";
import { useValidatePermission } from "core/features/auth/hooks/useValidatePermission";
import useMapAmount from "../../hooks/useMapAmount";

interface ModalDetailsTalhaoProps {
  mapRef: any;
  isOpen: boolean;
  talhaoId: string;
  talhaoCodigo: string;
  refetch: any;
  handleClose: () => void;
}

function ModalDetailsTalhao({ mapRef, isOpen, talhaoId, talhaoCodigo, refetch, handleClose }: ModalDetailsTalhaoProps) {

  const [expanded, setExpanded] = useState<number>();
  const [showVinculo, setShowVinculo] = useState(false);
  const [showPeriodo, setShowPeriodo] = useState(false);
  const [eventDeleteItem, setEventDeleteItem] = useState(false);
  const { setAmount } = useMapAmount();

  const { planoOperacionalIds } = useTalhoesStore();

  const { filters } = useFilters();
  const { filtersAdvanced } = filters;

  const { data, refetch: refetchPlanejamentos, isLoading: loadingPlanejamentos } = useGetPlanejamentosTalhao(talhaoId, filtersAdvanced.safraPeriodo);
  const { mutateAsync, isLoading } = useCancelarVinculoPlanejamentosTalhoes();
  const { hasPermission } = useValidatePermission();
  const disabledButtonDelete = !hasPermission(['PlanoOperacional.DesvincularTalhao']);

  function handleCheckColumns(index: number) {
    const planejamento = data[index];
    const operacoes = planejamento.planoOperacionalOperacoes;
    const vinculo = operacoes.some((o: any) => o.planoOperacionalReferencia === "Operação Vinculada");
    const periodo = operacoes.some((o: any) => o.planoOperacionalReferencia === "Data pré definida");
    setShowVinculo(vinculo);
    setShowPeriodo(periodo);
  }

  function handleExpand(index: number) {
    handleCheckColumns(index);
    setExpanded(expanded === index ? undefined : index);
  }

function handleFormatOperacoes(operacoes: any[]) {
  return operacoes.flatMap((item) => {
    const componentes = item.componentes ?? [];

    if (componentes.length === 0) {
      return [{ ...item, componentes: [] }];
    }

    return componentes.map((componente: any) => ({
      ...item,
      componentes: [componente]
    }));
  });
}

  async function handleUpdateMapAfterDelete(planoOperacionalId: string) {
    if (!mapRef.current) return;

    mapRef.current.data.forEach((feature: any) => {
      const id = feature.getProperty("id");
      const planos = feature.getProperty("planosoperacionais") ?? [];

      const isPlanoSelecionado = planoOperacionalIds.some(p => p.id === planoOperacionalId);
      const isTalhaoVinculado = planos.some((p: any) => p.PlanoOperacionalId === planoOperacionalId);
      if (id === talhaoId && isPlanoSelecionado && isTalhaoVinculado) {
        const novosPlanos = planos.filter((p: any) => p.PlanoOperacionalId !== planoOperacionalId);
        feature.setProperty("planosoperacionais", novosPlanos);
        if (feature.getProperty("planosoperacionais")?.length === 0) {
          mapRef.current!.data.overrideStyle(feature, {
            fillColor: "#CECECE",
            fillOpacity: 1,
            strokeColor: "#FFFFFF",
            strokeOpacity: 1,
            strokeWeight: 2,
          });
        }
      }
    });

    await refetch();
    await refetchPlanejamentos();
    setEventDeleteItem(true);
    showSuccess("Planejamento excluído com sucesso");
  }

  async function reset() {
    if (!eventDeleteItem) {
      return
    }
    if (!mapRef.current) return;

    mapRef.current.data.forEach((feature: any) => {
      const id = feature.getProperty("id");
      if (id === talhaoId) {
          mapRef.current!.data.overrideStyle(feature, {
            fillColor: "#CECECE",
            fillOpacity: 1,
            strokeColor: "#FFFFFF",
            strokeOpacity: 1,
            strokeWeight: 2,
          });

      }
      setEventDeleteItem(false);
    });
  }

  async function counterMap() {
    if (!mapRef.current) return;

    mapRef.current.data.forEach((feature: any) => {
      const id = feature.getProperty("id");
      const planos = feature.getProperty("planosoperacionais") ?? [];
      const filteredPlanos = planos.filter((item: any, index: number, array: any[]) => index === array.findIndex((i) => i.PlanoOperacionalId === item.PlanoOperacionalId));
      const amounted = filteredPlanos.filter((p: any) => planoOperacionalIds.some(o => o.id === p.PlanoOperacionalId)).length;
      if (id === talhaoId) {
        setAmount(talhaoId, amounted);
      }

    });
  }

  async function handleDeleteVinculo(planoOperacionalOperacoes: any[], planoOperacionalId: string) {
    const result = await showConfirm("Deseja excluir o planejamento do talhão?");
    if (!result.isConfirmed) return;

    try {
      const talhoes = planoOperacionalOperacoes?.flatMap(p => p?.talhoes);
      const talhoesFiltrados = talhoes?.filter(p => !p?.possuiVinculoOrdemServico && p?.status !== "Cancelada");
      const planoOperacionalOperacaoTalhaoIds = talhoesFiltrados?.map(t => t?.planoOperacionalOperacaoTalhaoId);
      const body = { planoOperacionalOperacaoTalhaoIds }
      await mutateAsync(body);
      await handleUpdateMapAfterDelete(planoOperacionalId);
    } catch (error) {
      console.log(error);
      showError("Erro ao excluir o planejamento do talhão");
    }
  }

  useEffect(() => {
    if (!loadingPlanejamentos && (!data || data?.length === 0)) {
      reset();
      handleClose();
    }

    if (data && data.length > 0) {
      counterMap();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <>
      {(isLoading || loadingPlanejamentos) && <LoadingCard />}

      <StyledModal
        centered
        width={"95%"}
        visible={isOpen}
        onCancel={handleClose}
        getContainer={document.body}
        title={`Planejamentos do Talhão ${talhaoCodigo}`}
        footer={<Button onClick={handleClose}>Fechar</Button>}
      >
        {!loadingPlanejamentos ? (
          <>
            <StyledCard>
              <Flex alignItems="center" gridGap={4} fontSize={16} fontWeight={500}>
                <MdAssignment color="#1565c0" size={18} />
                <p>{data?.length ?? 0} planejamentos vinculados</p>
              </Flex>
            </StyledCard>

            {data?.map((item: any, index: number) => (
              <>
                <Card
                  onClick={() => handleExpand(index)}
                  style={{ marginTop: 8, cursor: "pointer", backgroundColor: "#f5f5f5", borderRadius: 8 }}
                >
                  <Flex justifyContent="space-between" alignItems="center">
                    <Flex alignItems="center" gridGap={4}>
                      {expanded === index ? (
                        <MdKeyboardArrowDown color="#27ae60" size={22} />
                      ) : (
                        <MdOutlineKeyboardArrowRight color="#27ae60" size={22} />
                      )}
                      <b style={{ marginRight: 16 }}>{item.descricao}</b>
                      <Flex alignItems="center" gridGap={2}>
                        <FaLeaf size={16} color="#4caf50" />
                        <p style={{ color: "#4caf50", fontWeight: "bold" }}>{item.ocupacao}</p>
                      </Flex>
                      <Flex alignItems="center" gridGap={2}>
                        <MdList size={22} color="#757575" />
                        <p>{item.planoOperacionalOperacoes.length} operações</p>
                      </Flex>
                    </Flex>
                    <Flex alignItems="center" gridGap={4}>
                      <Tooltip title={disabledButtonDelete ? 'Você não possui permissão para excluir o planejamento' : item?.possuiVinculoOrdemServico ? "Plano já vinculado à uma O.S." : ""}>
                        <FaTrash
                          size={18}
                          color={item?.possuiVinculoOrdemServico || disabledButtonDelete ? "#cecece" : "#f00"}
                          cursor={item?.possuiVinculoOrdemServico || disabledButtonDelete ? "not-allowed" : "pointer"}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (disabledButtonDelete) return;
                            if (!item?.possuiVinculoOrdemServico) {
                              handleDeleteVinculo(item?.planoOperacionalOperacoes, item?.planoOperacionalId);
                            }
                          }}
                        />
                      </Tooltip>
                    </Flex>
                  </Flex>
                </Card>
                {expanded === index && (
                  <RoundedTable
                    key="planoOperacionalId"
                    dataSource={handleFormatOperacoes(item.planoOperacionalOperacoes)}
                    pagination={false}
                    columns={getColumns(data, showVinculo, showPeriodo)}
                    scroll={{ x: true, y: 320 }}
                    style={{ marginTop: 8 }}
                  />
                )}
              </>
            ))}
          </>
        ) : (
          <Flex justifyContent="center">
            <Spin />
          </Flex>
        )}
      </StyledModal>
    </>
  );
}

const StyledModal = styled(Modal)`
  .ant-modal-body {
    max-height: calc(90vh - 108px);
    overflow-y: scroll;
  }

  .ant-modal-content {
    border-radius: 10px;
  }

  .ant-modal-header {
    background-color: #12619c;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
  }

  .ant-modal-title {
    color: #fff;
  }

  .ant-modal-close-x {
    color: #fff;
  }
`;

const StyledCard = styled(Card)`
  background-color: #e3f2fd;
  color: #1565c0;
  border-left: 5px solid #1565c0;
  border-radius: 8px;

  .ant-card-body {
    padding: 14px;
  }
`;

const RoundedTable = styled(Table)`
  .ant-table {
    border-radius: 8px;
    overflow: hidden;
  }

  .ant-table-container {
    border-radius: 8px;
    overflow: hidden;
  }
`;

export default ModalDetailsTalhao;
